import { useEffect, useRef } from "react";
import FormField from "../../../../../FormField";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import "./style.css";

const days = ["S", "M", "T", "W", "T", "F", " S"];
const daysFullName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const getDate = (startDate) => {
  const startDateWeekday = new Date(startDate).toLocaleDateString("en-US", { weekday: "long" });
  const startDateDay = new Date(startDate).getDate();

  return {
    name: startDateWeekday,
    day: startDateDay,
  };
};

export const RecurrencePicker = ({
  setFieldValue,
  repetition,
  values,
  errors,
  endDate,
  startDate,
}) => {
  const hasMounted = useRef(false);
  const stringRepetitionValue = `${values.repetition}`;

  const handleOnChange = (index) => () => {
    const stringIndex = `${index}`;
    const repetitionValuesArray = stringRepetitionValue.split(",");

    if (repetitionValuesArray.includes(stringIndex)) {
      if (values.repeat === "weekly" && repetitionValuesArray.length === 1) return;

      if (values.repeat === "daily") {
        setFieldValue("repeat", "weekly");
      }

      return setFieldValue(
        repetition.name,
        repetitionValuesArray.filter((value) => value !== stringIndex).join(",")
      );
    }

    const newRepetitionValues = repetitionValuesArray.concat(index);

    if (newRepetitionValues.length === 7) {
      setFieldValue("repeat", "daily");
    }

    setFieldValue(repetition.name, newRepetitionValues.join(","));
  };

  const createMonthDateValue = () => {
    return {
      value: `${values.repetition}`,
      label: `On day ${values.repetition}`,
    };
  };

  const createMonthDayOptions = () => {
    const { day } = getDate(startDate);

    return [
      {
        value: `${day}`,
        label: `On day ${day}`,
      },
    ];
  };

  const renderSelections = () => {
    const mappedDays = days.map((name, index) => (
      <SoftBox key={name + index}>
        <input
          className="recurrence__values-checkbox"
          type="checkbox"
          id={name + index}
          style={{
            left: "-9999px",
            opacity: 0,
            position: "absolute",
          }}
          checked={stringRepetitionValue.includes(`${index}`)}
          onChange={handleOnChange(index)}
        />
        <SoftTypography
          sx={{
            borderRadius: "32px",
            cursor: "pointer",
            display: "grid",
            fontSize: "13px",
            fontWeight: 500,
            height: "34px",
            userSelect: "none",
            placeContent: "center",
            position: "relative",
            width: "34px",
          }}
          as="label"
          htmlFor={name + index}
        >
          {name}
        </SoftTypography>
        <div />
      </SoftBox>
    ));

    return mappedDays;
  };

  const renderRecurrences = () => {
    switch (values.repeat) {
      case "minutely":
      case "hourly":
        return (
          <SoftBox>
            <FormField
              type="number"
              placeholder={values.repeat === "minutely" ? "1-60" : "1-24"}
              label={repetition.label}
              name={repetition.name}
              error={!!errors.repetition}
              onChange={(e)=> {
                setFieldValue(repetition.name, e.target.value);
              }}
            />
          </SoftBox>
        );
      case "daily":
      case "weekly":
        return (
          <SoftBox>
            <SoftBox mb={1} lineHeight={0} display="inline-block">
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="capitalize"
              >
                Repetition
              </SoftTypography>
            </SoftBox>
            <SoftBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                marginLeft: "4px",
                gap: "8px",
              }}
            >
              {renderSelections()}
            </SoftBox>
          </SoftBox>
        );
      case "monthly":
        return (
          <SoftBox>
            <FormField
              type="select"
              label="Day"
              name={repetition.name}
              value={createMonthDateValue()}
              options={createMonthDayOptions()}
              onChange={(value) => setFieldValue(repetition.name, `${value}`)}
            />
          </SoftBox>
        );
    }

    return <></>;
  };

  const getExplainedRecurrences = () => {
    let recurrenceText = "";

    const getEndDateText = () => {
      const convertedEndDate = endDate ? new Date(endDate) : null;

      if (convertedEndDate) {
        const options = { year: "numeric", month: "short", day: "numeric" };
        const formattedDate = convertedEndDate.toLocaleDateString("en-US", options);

        return ` until ${formattedDate}`;
      }

      return "";
    };

    switch (values.repeat) {
      case "not_repeat":
        return <></>;
      case "minutely": {
        if (!values.repetition) {
          recurrenceText = "Occurs every minute";

          break;
        }

        const repetitionNumber = parseInt(values.repetition);

        recurrenceText = `Occurs every ${repetitionNumber} ${
          repetitionNumber > 1 ? " minutes" : " minute"
        }`;

        break;
      }
      case "hourly": {
        if (!values.repetition) {
          recurrenceText = "Occurs every hour";

          break;
        }

        const repetitionNumber = parseInt(values.repetition);

        recurrenceText = `Occurs every ${repetitionNumber} ${
          repetitionNumber > 1 ? " hours" : " hour"
        }`;

        break;
      }
      case "daily": {
        recurrenceText = "Occurs every day";

        break;
      }
      case "weekly": {
        const days = stringRepetitionValue
          .split(",")
          .map((value) => parseInt(value))
          .sort((a, b) => a - b);
        const mappedDays = days.map((day) => daysFullName[parseInt(day)]);

        if (mappedDays.length === 1) {
          recurrenceText = `Occurs on ${mappedDays[0]} every week`;
        } else if (mappedDays.length === 2) {
          recurrenceText = `Occurs on ${mappedDays[0]} and ${mappedDays[1]} every week`;
        } else {
          recurrenceText = `Occurs on ${mappedDays.join(", ")} every week`;
        }

        break;
      }
      case "monthly": {
        const { day } = getDate(startDate);
        recurrenceText = `Occurs on day ${day} every month`;

        break;
      }
    }

    return recurrenceText + getEndDateText();
  };

  useEffect(() => {
    if (!hasMounted.current) return;

    switch (values.repeat) {
      case "daily":
        setFieldValue(repetition.name, "0,1,2,3,4,5,6");

        return;
      case "weekly":
        if (startDate) {
          const options = { weekday: "long" };
          const startDateWeekday = new Date(startDate).toLocaleDateString("en-US", options);
          const dayIndex = daysFullName.findIndex((value) => value === startDateWeekday);

          if (dayIndex === -1) return;

          setFieldValue(repetition.name, `${dayIndex}`);
        }

        return;
      case "monthly": {
        const { day } = getDate(startDate);

        setFieldValue(repetition.name, `${day}`);

        return;
      }
    }

    if (!values.repeat) setFieldValue(repetition.name, "");
  }, [values.repeat, startDate]);

  useEffect(() => {
    if (!values.repetition || typeof values.repetition !== "string") return;

    setFieldValue(repetition.name, values.repetition);
  }, [values.repetition]);

  useEffect(() => {
    setTimeout(() => {
      hasMounted.current = true;
    }, 500);
  }, []);

  return (
    <SoftBox>
      <SoftBox>{renderRecurrences()}</SoftBox>
      <SoftTypography sx={{ fontSize: "14px", fontWeight: 500, margin: "16px 0 8px" }}>
        {getExplainedRecurrences()}
      </SoftTypography>
    </SoftBox>
  );
};
